/* eslint-disable no-unused-vars */
/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
import prepareTranslate from '@/helpers/dictionary';
import { ROLE_LIST, USER_STATUS } from '@/helpers/enums';
import { isEmail, isPassword, isPhone } from '@/helpers/is';
import routes from '@/routes';
import {
  Button,
  Grid,
  Group,
  PasswordInput,
  Progress,
  Select,
  Text,
  TextInput,
} from '@mantine/core';
import PropTypes from 'prop-types';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import userQuery from '@/api/queries/user';
import userMutations from '@/api/mutitations/user';
import { getStrength } from '@/pages/ResetPassword/funcs';
import DetailsModal from '../DetailsModal';
import Modal from '../Modal/Index';

const UserEdit = (props) => {
  const {
    opened,
    close: modalclose,
    id,
    setKey,
  } = props;

  const t = prepareTranslate();
  const navigate = useNavigate();
  const [loader, { open, close }] = useDisclosure(false);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [actionResultOpended,
    { open: actionResultOpen, close: actionResultClose },
  ] = useDisclosure();

  const resultActions = [
    <Button key='closeButton' w={'7rem'} onClick={() => actionResultClose()}>{t('global.close')}</Button>,
  ];

  const [resultLabel, setResultLabel] = useState('');

  const { useUpdateUser } = userMutations;
  const { mutateAsync } = useUpdateUser();

  const {
    data: userData,
    refetch,
  } = userQuery.useGetOne({ id });

  const STATUS = Object.fromEntries(
    USER_STATUS.map((status) => [status.value, status.label]),
  );
  const userIsActive = USER_STATUS[0].value;
  const userIsArchived = USER_STATUS[1].value;

  useEffect(() => {
    if (opened && id) {
      refetch();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, id]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        if (!userData) {
          navigate(routes.users.path);
        } else {
          form.setValues({
            firstName: userData.user?.firstName,
            lastName: userData.user?.lastName,
            userName: userData.user?.userName,
            role: userData.user?.role,
            email: userData.user?.email,
            phone: userData.user?.phone,
            isArchived: userData.user?.isArchived ? userIsArchived : userIsActive,
          });
          setLoading(false);
          setData(userData.user);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching user data:', error);
      }
    };
    if (userData) {
      fetchData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, navigate]);

  const form = useForm({
    initialValues: {
      firstName: '',
      lastName: '',
      userName: '',
      password: '',
      confirmPassword: '',
      role: 'administrator',
      isArchived: 'active',
      email: '',
      phone: '',
    },
    validate: {
      firstName: (value) => (value ? null : t('pages.useredit.validations.firstname')),
      lastName: (value) => (value ? null : t('pages.useredit.validations.lastname')),
      userName: (value) => (value ? null : t('pages.useredit.validations.username')),
      password: (value) => (!value || isPassword(value) ? null : t('pages.useredit.validations.password.required')),
      confirmPassword: (value, values) => (value !== values.password ? t('pages.useredit.validations.password.confirm') : null),
      role: (value) => (value ? null : t('pages.useredit.validations.role')),
      email: (value) => (isEmail(value) ? null : t('pages.useredit.validations.email')),
      phone: (value) => (isPhone(value) ? null : t('pages.useredit.validations.phone')),
    },
  });

  const handleUserEditClick = async () => {
    try {
      open();
      const {
        confirmPassword,
        password,
        isArchived,
        ...userValues
      } = form.values;

      const initialValues = {
        firstName: userData.user?.firstName,
        lastName: userData.user?.lastName,
        userName: userData.user?.userName,
        role: userData.user?.role,
        email: userData.user?.email,
        phone: userData.user?.phone,
        isArchived: userData.user?.isArchived ? userIsArchived : userIsActive,
      };
      const updatedValues = {};
      updatedValues.id = userData.user._id;

      if (isArchived === userIsArchived) {
        updatedValues.isArchived = true;
      } else {
        updatedValues.isArchived = false;
      }

      if (password) {
        updatedValues.password = password;
      }
      Object.keys(userValues).forEach((key) => {
        if (userValues[key] !== initialValues[key]) {
          updatedValues[key] = userValues[key];
        }
      });

      const updatedUser = await mutateAsync(updatedValues);

      if (!updatedUser) {
        setResultLabel(t('pages.useredit.notification.errortitle'));
        actionResultOpen();
        close();
        return;
      }
      setResultLabel(t('pages.useredit.notification.title'));
      actionResultOpen();
      modalclose();
      setKey((prev) => prev + 1);
      close();
    } catch (error) {
      setResultLabel(t('pages.useredit.notification.errortitle'));
      actionResultOpen();
      close();
    }
  };

  if (loading) {
    return null;
  }

  const strength = getStrength(form.values.password);

  const bars = Array(4)
    .fill(0)
    .map((_, index) => (
      <Progress
        key={index}
        styles={{ section: { transitionDuration: '0ms' } }}
        value={form.values.password > 0 && index === 0 ? 100 : strength >= ((index + 1) / 4) * 100 ? 100 : 0}
        color={strength > 80 ? 'teal' : strength > 50 ? 'yellow' : 'red'}
        size="4"
      />
    ));

  const fullname = `${data.firstName} ${data.lastName}`;

  const actions = [
      <Button w={'7rem'} key="primary" loading={loader} onClick={form.onSubmit(handleUserEditClick)}>
          {t('global.update')}
      </Button>,
    <Button w={'7rem'} key="cancel" variant="outline" onClick={() => modalclose()}>
      {t('global.cancel')}
    </Button>,
  ];
  if (!id) return null;

  return (
      <>
          <DetailsModal
              opened={opened}
              close={modalclose}
              actions={actions}
              label={t('pages.useredit.title', [fullname])}
          >
        <form>
              <Grid gutter="lg">
                <Grid.Col span={{ base: 3 }}>
                  <TextInput
                    autoComplete="off"
                    label={t('pages.useredit.form.firstname')}
                    required
                    {...form.getInputProps('firstName')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 3 }}>
                  <TextInput
                    autoComplete="off"
                    label={t('pages.useredit.form.lastname')}
                    required
                    {...form.getInputProps('lastName')}
                  />
                      </Grid.Col>
                      <Grid.Col span={{ base: 3 }}>
                  <TextInput
                    autoComplete="off"
                    label={t('pages.useredit.form.phone')}
                    required
                    {...form.getInputProps('phone')}
                  />
                </Grid.Col>
                      <Grid.Col span={{ base: 3 }}>
                  <TextInput
                    autoComplete="off"
                    label={t('pages.useredit.form.email')}
                    required
                    {...form.getInputProps('email')}
                  />
                      </Grid.Col>
                      <Grid.Col span={{ base: 3 }}>
                  <Select
                    label={t('pages.useredit.form.role')}
                    data={ROLE_LIST}
                    required
                    {...form.getInputProps('role')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 3 }}>
                  <Select
                    label={t('pages.useredit.form.status')}
                    data={USER_STATUS}
                    required
                    {...form.getInputProps('isArchived')}
                  />
                      </Grid.Col>

              </Grid>
                  <Text
                      fw={'bold'}
                      c={'var(--mantine-color-brand-10)'}
                      fs={'large'}
                      m={'var(--mantine-spacing-xl) 0 var(--mantine-spacing-md)'}>
                      {t('pages.useredit.form.chooseCredentials')}
                  </Text>
                  <Grid gutter="lg">

                <Grid.Col span={{ base: 3 }}>
                  <TextInput
                    autoComplete="off"
                    label={t('pages.useredit.form.username')}
                    required
                    {...form.getInputProps('userName')}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 3 }}>
                  <PasswordInput
                    autoComplete="off"
                    label={t('pages.useredit.form.password')}
                    required={false}
                    {...form.getInputProps('password')}
                  />
                  <Group gap={5} grow mt="xs" mb="md">
                    {bars}
                  </Group>
                </Grid.Col>
                <Grid.Col span={{ base: 3 }}>
                  <PasswordInput
                    autoComplete="off"
                    label={t('pages.useredit.form.confirmpassword')}
                    required={false}
                    {...form.getInputProps('confirmPassword')}
                  />
                </Grid.Col>
              </Grid>

              </form>
              </DetailsModal>
     <Modal
        opened={actionResultOpended}
        close={actionResultClose}
        actions={resultActions}
        label={resultLabel}
      />

    </>
  );
};

UserEdit.propTypes = {
  opened: PropTypes.bool,
  close: PropTypes.func,
  id: PropTypes.string,
  setKey: PropTypes.func,
};

export default UserEdit;
